import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Upload, Printer, FileDown, X, Truck, RotateCcw, Split } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { AnalyticsTab } from "@/components/admin/AnalyticsTab";
import { InventoryTab } from "@/components/admin/InventoryTab";
import { api, imgSrc, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY_FORM = { name: "", description: "", price: "", category: "Totes", image: "", images: [], stock: 1, featured: false, size: "", weight_oz: "", usps_rate: "", variants: [] };
const EMPTY_VARIANT = { id: "", name: "", stock: 0, price: "", image: "" };
const CATEGORIES = ["Totes", "Crossbody", "Buckets", "Clutches", "Purses", "Jewelry", "Cosmetics", "Clothing", "Home", "Furniture"];
const isVideo = (m) => /\.(mp4|webm|mov)$/i.test(m || "");

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [shipOrder, setShipOrder] = useState(null);
    const [shipTracking, setShipTracking] = useState("");
    const [shippingOrder, setShippingOrder] = useState(false);
    const [promos, setPromos] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [promoForm, setPromoForm] = useState({ code: "", percent: "" });
    const [promoSaving, setPromoSaving] = useState(false);
    const [labelOrder, setLabelOrder] = useState(null);
    const [labelRates, setLabelRates] = useState([]);
    const [labelSelected, setLabelSelected] = useState(null);
    const [labelLoading, setLabelLoading] = useState(false);
    const [labelBuying, setLabelBuying] = useState(false);
    const [labelResult, setLabelResult] = useState(null);
    const [refundOrder, setRefundOrder] = useState(null);
    const [refundAmount, setRefundAmount] = useState("");
    const [refunding, setRefunding] = useState(false);

    useEffect(() => {
        if (user === false) navigate("/admin");
    }, [user, navigate]);

    const loadProducts = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    const [selected, setSelected] = useState([]);
    const [mergeOpen, setMergeOpen] = useState(false);
    const [mergeForm, setMergeForm] = useState({ name: "", price: "", rows: [] });
    const [unmergeTarget, setUnmergeTarget] = useState(null);
    const [unmergeNames, setUnmergeNames] = useState([]);
    const [unmergeAssign, setUnmergeAssign] = useState({});
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkText, setBulkText] = useState("");

    const addBulkVariants = () => {
        const rows = bulkText
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => {
                const [name, stock, price] = line.split(",").map((s) => s.trim());
                return { id: "", name: name || "", stock: stock || "0", price: price || "", image: "" };
            })
            .filter((r) => r.name);
        if (rows.length === 0) {
            toast.error("No colors found — one per line, e.g. #6 Beige Beauty, 18");
            return;
        }
        setForm((f) => ({ ...f, variants: [...f.variants, ...rows] }));
        setBulkText("");
        setBulkOpen(false);
        toast.success(`${rows.length} colors added — set photos next to each one`);
    };

    const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    const openMerge = () => {
        const picks = selected.map((id) => products.find((p) => p.id === id)).filter(Boolean);
        if (picks.length < 2) return;
        const basePrice = picks[0].price;
        const rows = [];
        picks.forEach((p) => {
            const pImages = p.images?.length ? p.images : [p.image].filter(Boolean);
            if (p.variants?.length) {
                p.variants.forEach((v) => rows.push({ name: v.name, stock: v.stock, price: v.price != null ? String(v.price) : "", image: v.image || p.image, images: v.images?.length ? v.images : pImages }));
            } else {
                rows.push({ name: p.name, stock: p.stock, price: p.price !== basePrice ? String(p.price) : "", image: p.image, images: pImages });
            }
        });
        setMergeForm({ name: picks[0].name, price: String(basePrice), rows });
        setMergeOpen(true);
    };

    const setMergeRow = (i, field, value) => setMergeForm((f) => ({ ...f, rows: f.rows.map((r, x) => (x === i ? { ...r, [field]: value } : r)) }));

    const openUnmerge = (p) => {
        setUnmergeTarget(p);
        setUnmergeNames((p.variants || []).map((v) => v.name));
        setUnmergeAssign({});
    };

    const unmergeUnassigned = (p) => {
        if (!p) return [];
        const claimed = new Set();
        (p.variants || []).forEach((v) => {
            (v.images || []).forEach((im) => claimed.add(im));
            if (v.image) claimed.add(v.image);
        });
        return (p.images || []).filter((im) => !claimed.has(im));
    };

    const cycleAssign = (path) => {
        const n = unmergeTarget?.variants?.length || 1;
        setUnmergeAssign((a) => ({ ...a, [path]: a[path] == null ? 0 : (a[path] + 1) % n }));
    };

    const confirmUnmerge = async () => {
        const p = unmergeTarget;
        if (!p) return;
        try {
            for (let i = 0; i < (p.variants || []).length; i++) {
                const v = p.variants[i];
                const assigned = Object.entries(unmergeAssign).filter(([, idx]) => idx === i).map(([path]) => path);
                const vImages = [...new Set([v.image, ...(v.images || []), ...assigned].filter(Boolean))];
                const finalImages = vImages.length ? vImages : (p.images || []);
                await api.post("/products", {
                    name: unmergeNames[i]?.trim() || v.name,
                    description: p.description,
                    price: v.price != null ? v.price : p.price,
                    category: p.category,
                    image: finalImages[0] || p.image || "",
                    images: finalImages,
                    stock: v.stock,
                    featured: false,
                    size: p.size || "",
                    weight_oz: p.weight_oz || 0,
                    usps_rate: p.usps_rate || 0,
                    variants: [],
                });
            }
            await api.delete(`/products/${p.id}`);
            toast.success(`Split "${p.name}" back into ${p.variants.length} listings`);
            setUnmergeTarget(null);
            loadProducts();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const confirmMerge = async () => {
        const picks = selected.map((id) => products.find((p) => p.id === id)).filter(Boolean);
        const base = picks[0];
        const images = [...new Set(picks.flatMap((p) => (p.images?.length ? p.images : [p.image]).filter(Boolean)))];
        const payload = {
            name: mergeForm.name.trim() || base.name,
            description: base.description,
            price: parseFloat(mergeForm.price) || base.price,
            category: base.category,
            image: images[0] || "",
            images,
            stock: 0,
            featured: base.featured,
            size: base.size || "",
            weight_oz: base.weight_oz || 0,
            usps_rate: base.usps_rate || 0,
            variants: mergeForm.rows.filter((r) => r.name.trim()).map((r) => ({ id: "", name: r.name.trim(), stock: parseInt(r.stock) || 0, price: r.price === "" ? null : parseFloat(r.price), image: r.image || "", images: r.images || [] })),
        };
        try {
            await api.put(`/products/${base.id}`, payload);
            for (const p of picks.slice(1)) {
                await api.delete(`/products/${p.id}`);
            }
            toast.success(`Merged ${picks.length} listings into "${payload.name}" with ${payload.variants.length} colors`);
            setMergeOpen(false);
            setSelected([]);
            loadProducts();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };
    const loadOrders = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    const loadPromos = () => api.get("/promos").then((r) => setPromos(r.data)).catch(() => {});
    const loadSubscribers = () => api.get("/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});

    useEffect(() => {
        if (user) {
            loadProducts();
            loadOrders();
        }
    }, [user]);

    if (!user) return null;

    const printSlip = (o) => {
        const addr = o.shipping_address || {};
        const cityLine = [addr.city, addr.state, addr.postal_code].filter(Boolean).join(" ");
        const rows = o.items
            .map((i) => `<tr><td style="padding:8px 0;border-bottom:1px solid #ddd;">${i.name} × ${i.quantity}</td><td style="padding:8px 0;border-bottom:1px solid #ddd;text-align:right;">$${(i.price * i.quantity).toFixed(2)}</td></tr>`)
            .join("");
        const win = window.open("", "_blank", "width=700,height=900");
        win.document.write(`<!doctype html><html><head><title>Kichazone Packing Slip</title></head>
<body style="font-family:Georgia,serif;color:#1A1714;padding:48px;max-width:600px;margin:0 auto;">
  <div style="border-bottom:2px solid #1A1714;padding-bottom:16px;margin-bottom:24px;">
    <span style="font-size:28px;">Kicha<span style="font-style:italic;color:#B08D57;">zone</span></span>
    <span style="float:right;font-family:Courier,monospace;font-size:11px;letter-spacing:2px;text-align:right;">PACKING SLIP<br>${new Date(o.created_at).toLocaleDateString()}</span>
  </div>
  <p style="font-family:Courier,monospace;font-size:10px;letter-spacing:2px;color:#5C564D;margin:0 0 6px;">SHIP TO</p>
  <p style="font-size:16px;line-height:24px;margin:0 0 24px;">
    <strong>${o.customer_name || "Customer"}</strong><br>
    ${addr.line1 || ""}${addr.line2 ? "<br>" + addr.line2 : ""}<br>
    ${cityLine}${addr.country ? "<br>" + addr.country : ""}<br>
    <span style="font-size:13px;color:#5C564D;">${o.customer_email || ""}</span>
  </p>
  <p style="font-family:Courier,monospace;font-size:10px;letter-spacing:2px;color:#5C564D;margin:0 0 6px;">ITEMS</p>
  <table style="width:100%;border-collapse:collapse;font-size:15px;">${rows}</table>
  <table style="width:100%;margin-top:12px;font-size:14px;">
    <tr><td style="font-family:Courier,monospace;font-size:11px;letter-spacing:2px;">USPS SHIPPING</td><td style="text-align:right;">${o.shipping_amount ? "$" + o.shipping_amount.toFixed(2) : "FREE"}</td></tr>
    <tr><td style="font-family:Courier,monospace;font-size:11px;letter-spacing:2px;padding-top:6px;">TOTAL</td><td style="text-align:right;font-size:20px;padding-top:6px;">$${o.amount_total.toFixed(2)}</td></tr>
  </table>
  ${o.tracking_number ? `<p style="font-family:Courier,monospace;font-size:11px;letter-spacing:1px;margin-top:24px;">TRACKING — ${o.tracking_number}</p>` : ""}
  <p style="margin-top:40px;font-style:italic;color:#5C564D;">Made with creativity, chosen with care. — Kichazone</p>
  <script>window.onload = function(){ window.print(); };</script>
</body></html>`);
        win.document.close();
    };

    const submitShip = async () => {
        if (!shipTracking.trim()) {
            toast.error("Enter a tracking number");
            return;
        }
        setShippingOrder(true);
        try {
            await api.post(`/orders/${shipOrder.id}/ship`, { tracking_number: shipTracking.trim() });
            toast.success("Marked as shipped — customer emailed the tracking number");
            setShipOrder(null);
            setShipTracking("");
            loadOrders();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setShippingOrder(false);
        }
    };

    const createPromo = async () => {
        if (!promoForm.code.trim() || !promoForm.percent) {
            toast.error("Enter a code and a discount percent");
            return;
        }
        setPromoSaving(true);
        try {
            await api.post("/promos", { code: promoForm.code.trim(), percent_off: parseFloat(promoForm.percent) });
            toast.success(`Code ${promoForm.code.toUpperCase()} is live`);
            setPromoForm({ code: "", percent: "" });
            loadPromos();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setPromoSaving(false);
        }
    };

    const deactivatePromo = async (p) => {
        if (!window.confirm(`Deactivate code "${p.code}"? Existing customers can't use it anymore.`)) return;
        try {
            await api.delete(`/promos/${p.id}`);
            toast.success("Code deactivated");
            loadPromos();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const openLabel = async (o) => {
        setLabelOrder(o);
        setLabelRates([]);
        setLabelSelected(null);
        setLabelResult(null);
        setLabelLoading(true);
        try {
            const res = await api.get(`/orders/${o.id}/rates`);
            setLabelRates(res.data.rates);
            setLabelSelected(res.data.rates[0] || null);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLabelLoading(false);
        }
    };

    const buyLabel = async () => {
        if (!labelSelected) return;
        setLabelBuying(true);
        try {
            const res = await api.post(`/orders/${labelOrder.id}/buy-label`, { rate_id: labelSelected.id, provider: labelSelected.provider, service: labelSelected.service });
            setLabelResult(res.data);
            toast.success(`Label created — ${res.data.carrier} tracking saved, order marked shipped`);
            loadOrders();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLabelBuying(false);
        }
    };

    const openRefund = (o) => {
        setRefundOrder(o);
        setRefundAmount((o.amount_total - (o.refunded_total || 0)).toFixed(2));
    };

    const submitRefund = async () => {
        setRefunding(true);
        try {
            const amount = parseFloat(refundAmount);
            const res = await api.post(`/orders/${refundOrder.id}/refund`, { amount });
            toast.success(`$${res.data.refunded.toFixed(2)} refunded to the customer's card`);
            setRefundOrder(null);
            loadOrders();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setRefunding(false);
        }
    };

    const openAdd = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setDialogOpen(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({ name: p.name, description: p.description, price: String(p.price), category: p.category, image: p.image, images: p.images?.length ? p.images : (p.image ? [p.image] : []), stock: p.stock, featured: p.featured, size: p.size || "", weight_oz: p.weight_oz ?? "", usps_rate: p.usps_rate ?? "", variants: (p.variants || []).map((v) => ({ id: v.id, name: v.name, stock: v.stock, price: v.price != null ? String(v.price) : "", image: v.image || "" })) });
        setDialogOpen(true);
    };

    const handleFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const paths = [];
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const res = await api.post("/upload", fd);
                paths.push(res.data.path);
            }
            setForm((f) => ({ ...f, images: [...f.images, ...paths], image: (f.images[0] || paths[0]) }));
            toast.success(`${paths.length} file${paths.length > 1 ? "s" : ""} uploaded`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };
    const handleVariantFile = async (index, e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await api.post("/upload", fd);
            setForm((f) => ({ ...f, variants: f.variants.map((v, i) => (i === index ? { ...v, image: res.data.path } : v)) }));
            toast.success("Color photo uploaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const setVariantField = (index, field, value) => {
        setForm((f) => ({ ...f, variants: f.variants.map((v, i) => (i === index ? { ...v, [field]: value } : v)) }));
    };


    const save = async () => {
        if (!form.name || !form.price) {
            toast.error("Name and price are required");
            return;
        }
        setSaving(true);
        const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock) || 0, weight_oz: parseFloat(form.weight_oz) || 0, usps_rate: parseFloat(form.usps_rate) || 0, image: form.images[0] || form.image || "", variants: form.variants.filter((v) => v.name.trim()).map((v) => ({ id: v.id || "", name: v.name.trim(), stock: parseInt(v.stock) || 0, price: v.price === "" || v.price == null ? null : parseFloat(v.price), image: v.image || "" })) };
        try {
            if (editing) await api.put(`/products/${editing.id}`, payload);
            else await api.post("/products", payload);
            toast.success(editing ? "Product updated" : "Product added");
            setDialogOpen(false);
            loadProducts();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.name}"? This can't be undone.`)) return;
        try {
            await api.delete(`/products/${p.id}`);
            toast.success("Product deleted");
            loadProducts();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const deliverInPerson = async (o) => {
        if (!window.confirm(`Mark ${o.customer_name || o.customer_email}'s order as handed over in person? The customer gets a delivery email.`)) return;
        try {
            await api.post(`/orders/${o.id}/deliver-in-person`);
            toast.success("Marked as delivered in person");
            loadOrders();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    return (
        <main data-testid="admin-dashboard" className="min-h-screen bg-sand">
            <header className="border-b border-line bg-white">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-10">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Kichazone — Studio</p>
                        <h1 className="mt-1 font-serif text-2xl font-medium tracking-tight" data-testid="admin-greeting">Hello, {user.name}</h1>
                    </div>
                    <button
                        onClick={() => { logout(); navigate("/admin"); }}
                        data-testid="admin-logout-button"
                        className="flex items-center gap-2 border border-espresso px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-espresso hover:text-sand"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>
            </header>
            <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
                <Tabs defaultValue="products">
                    <TabsList className="rounded-none border border-line bg-white">
                        <TabsTrigger value="products" data-testid="tab-products" className="rounded-none font-mono text-xs uppercase tracking-[0.2em]">Products</TabsTrigger>
                        <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none font-mono text-xs uppercase tracking-[0.2em]" onClick={loadOrders}>Orders</TabsTrigger>
                        <TabsTrigger value="promos" data-testid="tab-promos" className="rounded-none font-mono text-xs uppercase tracking-[0.2em]" onClick={loadPromos}>Promos</TabsTrigger>
                        <TabsTrigger value="emails" data-testid="tab-emails" className="rounded-none font-mono text-xs uppercase tracking-[0.2em]" onClick={loadSubscribers}>Emails</TabsTrigger>
                        <TabsTrigger value="inventory" data-testid="tab-inventory" className="rounded-none font-mono text-xs uppercase tracking-[0.2em]">Inventory</TabsTrigger>
                        <TabsTrigger value="analytics" data-testid="tab-analytics" className="rounded-none font-mono text-xs uppercase tracking-[0.2em]">Analytics</TabsTrigger>
                    </TabsList>
                    <TabsContent value="products" className="mt-8">
                        <div className="mb-6 flex items-center justify-between">
                            <p className="font-mono text-xs uppercase tracking-[0.2em] text-clay" data-testid="admin-product-count">{products.length} products</p>
                            <div className="flex items-center gap-3">
                                {selected.length >= 2 && (
                                    <button
                                        onClick={openMerge}
                                        data-testid="merge-products-button"
                                        className="flex items-center gap-2 border border-espresso px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-espresso hover:text-sand"
                                    >
                                        Merge {selected.length} into one
                                    </button>
                                )}
                                <button
                                    onClick={openAdd}
                                    data-testid="add-product-button"
                                    className="flex items-center gap-2 bg-espresso px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-crimson"
                                >
                                    <Plus size={14} /> Add product
                                </button>
                            </div>
                        </div>
                        {selected.length > 0 && (
                            <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.15em] text-clay" data-testid="merge-hint">
                                {selected.length} selected — tick 2 or more listings to merge them into one product with color cards
                            </p>
                        )}
                        <div className="overflow-x-auto border border-line bg-white">
                            <table className="w-full text-left text-sm" data-testid="products-table">
                                <thead>
                                    <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
                                        <th className="px-3 py-4"></th>
                                        <th className="px-5 py-4">Photo</th>
                                        <th className="px-5 py-4">Name</th>
                                        <th className="px-5 py-4">Category</th>
                                        <th className="px-5 py-4">Price</th>
                                        <th className="px-5 py-4">Stock</th>
                                        <th className="px-5 py-4">Ship</th>
                                        <th className="px-5 py-4">Featured</th>
                                        <th className="px-5 py-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((p) => (
                                        <tr key={p.id} className={`border-b border-line last:border-0 ${selected.includes(p.id) ? "bg-gold/10" : ""}`} data-testid={`admin-product-row-${p.id}`}>
                                            <td className="px-3 py-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selected.includes(p.id)}
                                                    onChange={() => toggleSelect(p.id)}
                                                    data-testid={`select-product-${p.id}`}
                                                    className="h-4 w-4 accent-[#1A1714]"
                                                />
                                            </td>
                                            <td className="px-5 py-3">
                                                <div className="h-14 w-11 overflow-hidden bg-line/40">
                                                    <img src={imgSrc(p.image)} alt="" className="h-full w-full object-cover" />
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 font-medium">{p.name}</td>
                                            <td className="px-5 py-3 font-mono text-xs uppercase tracking-wide text-clay">{p.category}</td>
                                            <td className="px-5 py-3 font-mono text-sm">${p.price.toFixed(2)}</td>
                                            <td className="px-5 py-3">{p.stock}{(p.variants?.length > 0) && <span className="ml-1 font-mono text-[10px] uppercase text-clay">· {p.variants.length} colors</span>}</td>
                                            <td className="px-5 py-3 font-mono text-xs">{p.usps_rate ? `$${p.usps_rate.toFixed(2)}` : "—"}</td>
                                            <td className="px-5 py-3">{p.featured ? <span className="bg-ochre/20 px-2 py-1 font-mono text-[10px] uppercase tracking-wide">Yes</span> : "—"}</td>
                                            <td className="px-5 py-3">
                                                <div className="flex justify-end gap-2">
                                                    {p.variants?.length > 0 && (
                                                        <button
                                                            onClick={() => openUnmerge(p)}
                                                            data-testid={`unmerge-${p.id}`}
                                                            title="Unmerge — split colors back into separate listings"
                                                            className="border border-line p-2 transition-colors hover:bg-gold hover:text-espresso"
                                                        >
                                                            <Split size={14} />
                                                        </button>
                                                    )}
                                                    <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="border border-line p-2 transition-colors hover:bg-espresso hover:text-sand">
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button onClick={() => remove(p)} data-testid={`delete-product-${p.id}`} className="border border-line p-2 transition-colors hover:bg-crimson hover:text-sand">
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {products.length === 0 && (
                                        <tr><td colSpan={8} className="px-5 py-10 text-center font-serif text-lg italic text-clay">No products yet — add your first bag.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </TabsContent>
                    <TabsContent value="orders" className="mt-8">
                        <div className="overflow-x-auto border border-line bg-white">
                            <table className="w-full text-left text-sm" data-testid="orders-table">
                                <thead>
                                    <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
                                        <th className="px-5 py-4">Date</th>
                                        <th className="px-5 py-4">Items</th>
                                        <th className="px-5 py-4">Customer</th>
                                        <th className="px-5 py-4">Total</th>
                                        <th className="px-5 py-4">Status</th>
                                        <th className="px-5 py-4">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map((o) => (
                                        <tr key={o.id} className="border-b border-line last:border-0" data-testid={`admin-order-row-${o.id}`}>
                                            <td className="px-5 py-3 font-mono text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                                            <td className="px-5 py-3">{o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</td>
                                            <td className="px-5 py-3">
                                                <p className="font-medium">{o.customer_name || "—"}</p>
                                                <p className="font-mono text-xs text-clay">{o.customer_email || ""}</p>
                                            </td>
                                            <td className="px-5 py-3 font-mono text-sm">${o.amount_total.toFixed(2)}</td>
                                            <td className="px-5 py-3">
                                                <span className={`px-2 py-1 font-mono text-[10px] uppercase tracking-wide ${["shipped", "delivered"].includes(o.status) ? "bg-green-100 text-green-800" : o.status === "refunded" ? "bg-crimson/10 text-crimson" : o.status === "partially_refunded" ? "bg-ochre/20 text-espresso" : o.payment_status === "paid" ? "bg-gold/20 text-espresso" : "bg-line/60 text-clay"}`}>
                                                    {o.status === "partially_refunded" ? "partial refund" : ["shipped", "delivered", "refunded"].includes(o.status) ? o.status : o.payment_status}
                                                </span>
                                                {(o.refunded_total || 0) > 0 && o.status === "partially_refunded" && (
                                                    <p className="mt-1 font-mono text-[10px] text-clay">-${o.refunded_total.toFixed(2)}</p>
                                                )}
                                            </td>
                                            <td className="px-5 py-3">
                                                <div className="flex items-center gap-2">
                                                    {o.payment_status === "paid" && !o.tracking_number && (
                                                        <button
                                                            onClick={() => openLabel(o)}
                                                            data-testid={`create-label-${o.id}`}
                                                            title="Create USPS shipping label"
                                                            className="border border-espresso p-2 transition-colors hover:bg-espresso hover:text-sand"
                                                        >
                                                            <Truck size={13} />
                                                        </button>
                                                    )}
                                                    {o.payment_status === "paid" && o.status !== "shipped" && o.status !== "delivered" && (
                                                        <button
                                                            onClick={() => { setShipOrder(o); setShipTracking(""); }}
                                                            data-testid={`ship-order-${o.id}`}
                                                            className="border border-espresso px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors hover:bg-espresso hover:text-sand"
                                                        >
                                                            Mark shipped
                                                        </button>
                                                    )}
                                                    {o.payment_status === "paid" && o.status !== "shipped" && o.status !== "delivered" && (
                                                        <button
                                                            onClick={() => deliverInPerson(o)}
                                                            data-testid={`deliver-personal-${o.id}`}
                                                            title="Handed over in person — no shipping or tracking needed"
                                                            className="border border-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors hover:bg-gold hover:text-espresso"
                                                        >
                                                            In person
                                                        </button>
                                                    )}
                                                    {o.status === "shipped" && (
                                                        <span className="font-mono text-[10px] text-clay" data-testid={`tracking-${o.id}`}>{o.tracking_number}</span>
                                                    )}
                                                    {o.status === "delivered" && (
                                                        <span className="font-mono text-[10px] uppercase tracking-wide text-clay" data-testid={`delivered-note-${o.id}`}>Handed over in person</span>
                                                    )}
                                                    {o.payment_status === "paid" && (
                                                        <button
                                                            onClick={() => printSlip(o)}
                                                            data-testid={`print-slip-${o.id}`}
                                                            title="Print packing slip"
                                                            className="border border-line p-2 transition-colors hover:bg-espresso hover:text-sand"
                                                        >
                                                            <Printer size={13} />
                                                        </button>
                                                    )}
                                                    {o.label_path && (
                                                        <a
                                                            href={imgSrc(o.label_path)}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            title="Shipping label PDF"
                                                            data-testid={`label-${o.id}`}
                                                            className="border border-line p-2 transition-colors hover:bg-espresso hover:text-sand"
                                                        >
                                                            <FileDown size={13} />
                                                        </a>
                                                    )}
                                                    {o.payment_status === "paid" && o.status !== "refunded" && (
                                                        <button
                                                            onClick={() => openRefund(o)}
                                                            data-testid={`refund-${o.id}`}
                                                            title="Refund to customer's card"
                                                            className="border border-line p-2 transition-colors hover:bg-crimson hover:text-sand"
                                                        >
                                                            <RotateCcw size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {orders.length === 0 && (
                                        <tr><td colSpan={6} className="px-5 py-10 text-center font-serif text-lg italic text-clay">No orders yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </TabsContent>
                    <TabsContent value="promos" className="mt-8">
                        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
                            <div className="border border-line bg-white p-8 lg:col-span-4">
                                <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay">New promo code</p>
                                <label className="mt-6 block font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Code</label>
                                <input
                                    value={promoForm.code}
                                    onChange={(e) => setPromoForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                                    data-testid="promo-code-input"
                                    className="mt-2 w-full border border-line bg-sand px-4 py-3 font-mono text-sm uppercase outline-none focus:border-espresso"
                                    placeholder="KICHA10"
                                />
                                <label className="mt-5 block font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Discount (%)</label>
                                <input
                                    type="number" min="1" max="100"
                                    value={promoForm.percent}
                                    onChange={(e) => setPromoForm((f) => ({ ...f, percent: e.target.value }))}
                                    data-testid="promo-percent-input"
                                    className="mt-2 w-full border border-line bg-sand px-4 py-3 text-sm outline-none focus:border-espresso"
                                    placeholder="10"
                                />
                                <button
                                    onClick={createPromo}
                                    disabled={promoSaving}
                                    data-testid="promo-create-button"
                                    className="mt-6 w-full bg-espresso py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-gold hover:text-espresso disabled:opacity-60"
                                >
                                    {promoSaving ? "Creating…" : "Create code"}
                                </button>
                                <p className="mt-4 font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-clay">
                                    Customers enter the code on the secure payment page — one use per customer
                                </p>
                            </div>
                            <div className="overflow-x-auto border border-line bg-white lg:col-span-8">
                                <table className="w-full text-left text-sm" data-testid="promos-table">
                                    <thead>
                                        <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
                                            <th className="px-5 py-4">Code</th>
                                            <th className="px-5 py-4">Discount</th>
                                            <th className="px-5 py-4">Created</th>
                                            <th className="px-5 py-4 text-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {promos.map((p) => (
                                            <tr key={p.id} className="border-b border-line last:border-0" data-testid={`promo-row-${p.id}`}>
                                                <td className="px-5 py-3 font-mono text-sm font-medium">{p.code}</td>
                                                <td className="px-5 py-3">{p.percent_off}% off</td>
                                                <td className="px-5 py-3 font-mono text-xs text-clay">{new Date(p.created_at).toLocaleDateString()}</td>
                                                <td className="px-5 py-3 text-right">
                                                    <button
                                                        onClick={() => deactivatePromo(p)}
                                                        data-testid={`deactivate-promo-${p.id}`}
                                                        className="border border-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors hover:bg-crimson hover:text-sand"
                                                    >
                                                        Deactivate
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {promos.length === 0 && (
                                            <tr><td colSpan={4} className="px-5 py-10 text-center font-serif text-lg italic text-clay">No codes yet — create your launch offer.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </TabsContent>
                    <TabsContent value="emails" className="mt-8">
                        <div className="overflow-x-auto border border-line bg-white">
                            <table className="w-full text-left text-sm" data-testid="subscribers-table">
                                <thead>
                                    <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
                                        <th className="px-5 py-4">Email</th>
                                        <th className="px-5 py-4">Joined</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subscribers.map((s) => (
                                        <tr key={s.email} className="border-b border-line last:border-0" data-testid={`subscriber-${s.email}`}>
                                            <td className="px-5 py-3">{s.email}</td>
                                            <td className="px-5 py-3 font-mono text-xs text-clay">{new Date(s.created_at).toLocaleDateString()}</td>
                                        </tr>
                                    ))}
                                    {subscribers.length === 0 && (
                                        <tr><td colSpan={2} className="px-5 py-10 text-center font-serif text-lg italic text-clay">No subscribers yet — the signup box is on your homepage.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-clay" data-testid="subscriber-count">{subscribers.length} subscriber{subscribers.length === 1 ? "" : "s"}</p>
                    </TabsContent>
                    <TabsContent value="inventory" className="mt-8">
                        <InventoryTab />
                    </TabsContent>
                    <TabsContent value="analytics" className="mt-8">
                        <AnalyticsTab />
                    </TabsContent>
                </Tabs>
            </div>
            <Dialog open={!!unmergeTarget} onOpenChange={(open) => !open && setUnmergeTarget(null)}>
                <DialogContent className="max-w-lg rounded-none border-espresso bg-sand" data-testid="unmerge-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl font-light">Unmerge — back to separate listings</DialogTitle>
                    </DialogHeader>
                    {unmergeTarget && (
                        <div className="flex flex-col gap-4">
                            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-clay">
                                Each color becomes its own listing again with its photos, price &amp; stock — the merged listing is removed
                            </p>
                            <div className="flex max-h-64 flex-col gap-2 overflow-y-auto">
                                {unmergeTarget.variants.map((v, i) => (
                                    <div key={v.id} className="flex items-center gap-2 border border-line bg-white p-2" data-testid={`unmerge-row-${i}`}>
                                        <div className="h-12 w-9 shrink-0 overflow-hidden bg-line/40">
                                            <img src={imgSrc(v.image || v.images?.[0] || unmergeTarget.image)} alt="" className="h-full w-full object-cover" />
                                        </div>
                                        <input
                                            value={unmergeNames[i] ?? v.name}
                                            onChange={(e) => setUnmergeNames((n) => n.map((x, xi) => (xi === i ? e.target.value : x)))}
                                            data-testid={`unmerge-name-${i}`}
                                            className="min-w-0 flex-1 border border-line px-2.5 py-2 text-xs outline-none focus:border-espresso"
                                        />
                                        <span className="shrink-0 font-mono text-[10px] uppercase text-clay">
                                            {[...new Set([v.image, ...(v.images || []), ...Object.entries(unmergeAssign).filter(([, idx]) => idx === i).map(([path]) => path)].filter(Boolean))].length} photos · {v.stock} in stock · ${(v.price != null ? v.price : unmergeTarget.price).toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            {unmergeUnassigned(unmergeTarget).length > 0 && (
                                <div data-testid="unmerge-photo-pool">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-clay">
                                        Remaining photos — tap one to choose which color keeps it (tap again to change)
                                    </p>
                                    <div className="mt-2 flex max-h-44 flex-wrap gap-2 overflow-y-auto">
                                        {unmergeUnassigned(unmergeTarget).map((path, i) => (
                                            <button
                                                key={path}
                                                type="button"
                                                onClick={() => cycleAssign(path)}
                                                data-testid={`unassign-photo-${i}`}
                                                className="relative h-20 w-16 overflow-hidden border border-line transition-colors hover:border-espresso"
                                            >
                                                <img src={imgSrc(path)} alt="" className="h-full w-full object-cover" />
                                                <span className="absolute inset-x-0 bottom-0 bg-espresso/85 px-1 py-0.5 text-center font-mono text-[8px] uppercase tracking-wide text-sand" data-testid={`unassign-chip-${i}`}>
                                                    {unmergeAssign[path] != null ? (unmergeNames[unmergeAssign[path]] || unmergeTarget.variants[unmergeAssign[path]].name) : "tap to assign"}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <button
                                onClick={confirmUnmerge}
                                data-testid="unmerge-confirm-button"
                                className="w-full bg-espresso py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-crimson"
                            >
                                Split into {unmergeTarget.variants.length} listings
                            </button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
                <DialogContent className="max-w-lg rounded-none border-espresso bg-sand" data-testid="merge-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl font-light">Merge into one product</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4">
                        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-clay">
                            Each listing becomes a tappable color card, Amazon style — the extra listings are removed afterwards
                        </p>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Product name</label>
                            <input
                                value={mergeForm.name}
                                onChange={(e) => setMergeForm((f) => ({ ...f, name: e.target.value }))}
                                data-testid="merge-name-input"
                                className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso"
                            />
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Base price (USD)</label>
                            <input
                                type="number" min="0" step="0.01"
                                value={mergeForm.price}
                                onChange={(e) => setMergeForm((f) => ({ ...f, price: e.target.value }))}
                                data-testid="merge-price-input"
                                className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso"
                            />
                        </div>
                        <div className="flex max-h-64 flex-col gap-2 overflow-y-auto" data-testid="merge-rows">
                            {mergeForm.rows.map((r, i) => (
                                <div key={i} className="flex items-center gap-2 border border-line bg-white p-2" data-testid={`merge-row-${i}`}>
                                    <div className="h-12 w-9 shrink-0 overflow-hidden bg-line/40">
                                        <img src={imgSrc(r.image)} alt="" className="h-full w-full object-cover" />
                                    </div>
                                    <input
                                        value={r.name}
                                        onChange={(e) => setMergeRow(i, "name", e.target.value)}
                                        placeholder="Color name"
                                        title="Color name shown on the card"
                                        data-testid={`merge-row-name-${i}`}
                                        className="min-w-0 flex-1 border border-line px-2.5 py-2 text-xs outline-none focus:border-espresso"
                                    />
                                    <input
                                        type="number" min="0"
                                        value={r.stock}
                                        onChange={(e) => setMergeRow(i, "stock", e.target.value)}
                                        title="Stock for this color"
                                        data-testid={`merge-row-stock-${i}`}
                                        className="w-16 border border-line px-2.5 py-2 text-xs outline-none focus:border-espresso"
                                    />
                                    <input
                                        type="number" min="0" step="0.01"
                                        value={r.price}
                                        onChange={(e) => setMergeRow(i, "price", e.target.value)}
                                        placeholder="$ base"
                                        title="Price — leave blank to use the base price"
                                        data-testid={`merge-row-price-${i}`}
                                        className="w-20 border border-line px-2.5 py-2 text-xs outline-none focus:border-espresso"
                                    />
                                </div>
                            ))}
                        </div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-clay">Each row = one color card · edit the name to just the shade (e.g. #23 Bloodshot)</p>
                        <button
                            onClick={confirmMerge}
                            data-testid="merge-confirm-button"
                            className="w-full bg-espresso py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-gold hover:text-espresso"
                        >
                            Merge into one listing
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none border-line bg-sand sm:max-w-lg" data-testid="product-form-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl font-medium tracking-tight">
                            {editing ? "Edit product" : "New product"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="mt-2 flex flex-col gap-5">
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Photos &amp; videos</label>
                            <div className="mt-2 flex flex-wrap items-center gap-3">
                                {form.images.map((m, i) => (
                                    <div key={m} className="relative h-24 w-[72px] overflow-hidden border border-line bg-line/40">
                                        {isVideo(m) ? (
                                            <video src={imgSrc(m)} className="h-full w-full object-cover" muted data-testid={`media-preview-${i}`} />
                                        ) : (
                                            <img src={imgSrc(m)} alt="" className="h-full w-full object-cover" data-testid={`media-preview-${i}`} />
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, x) => x !== i) }))}
                                            data-testid={`remove-media-${i}`}
                                            className="absolute right-0 top-0 bg-espresso/80 p-0.5 text-sand transition-colors hover:bg-crimson"
                                        >
                                            <X size={12} />
                                        </button>
                                        {i === 0 && <span className="absolute bottom-0 left-0 bg-gold px-1 font-mono text-[8px] uppercase tracking-wide text-espresso">Main</span>}
                                    </div>
                                ))}
                                <label className="flex h-24 w-[72px] cursor-pointer flex-col items-center justify-center gap-1 border border-dashed border-line text-clay transition-colors hover:border-espresso hover:text-espresso">
                                    <Upload size={16} />
                                    <span className="font-mono text-[8px] uppercase tracking-wide">{uploading ? "…" : "Add"}</span>
                                    <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleFiles} data-testid="product-image-input" />
                                </label>
                            </div>
                            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-clay">Add multiple photos or videos — the first one is the cover</p>
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Name</label>
                            <input
                                value={form.name}
                                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                data-testid="product-name-input"
                                className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso"
                                placeholder="The Market Tote"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Price (USD)</label>
                                <input
                                    type="number" min="0" step="0.01"
                                    value={form.price}
                                    onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                                    data-testid="product-price-input"
                                    className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso"
                                    placeholder="68.00"
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Stock</label>
                                {form.variants.length > 0 ? (
                                    <p className="mt-2 border border-line bg-line/30 px-4 py-3 font-mono text-xs text-clay" data-testid="stock-auto-note">
                                        Auto — sum of colors below
                                    </p>
                                ) : (
                                    <input
                                        type="number" min="0"
                                        value={form.stock}
                                        onChange={(e) => setForm((f) => ({ ...f, stock: e.target.value }))}
                                        data-testid="product-stock-input"
                                        className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso"
                                    />
                                )}
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Color options (optional)</label>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setBulkOpen((o) => !o)}
                                        data-testid="bulk-add-toggle"
                                        className={`flex items-center gap-1 border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${bulkOpen ? "border-espresso bg-espresso text-sand" : "border-line hover:border-espresso"}`}
                                    >
                                        Bulk add
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setForm((f) => ({ ...f, variants: [...f.variants, { ...EMPTY_VARIANT }] }))}
                                        data-testid="add-variant-button"
                                        className="flex items-center gap-1 border border-espresso px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors hover:bg-espresso hover:text-sand"
                                    >
                                        <Plus size={12} /> Add color
                                    </button>
                                </div>
                            </div>
                            {bulkOpen && (
                                <div className="mt-3 border border-line bg-white p-3" data-testid="bulk-add-box">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-clay">
                                        One color per line — name, stock, price (stock &amp; price optional)
                                    </p>
                                    <textarea
                                        value={bulkText}
                                        onChange={(e) => setBulkText(e.target.value)}
                                        rows={6}
                                        placeholder={"#1 Rose Beauty, 25\n#2 Chocolate, 25\n#3 Nude Beauty, 25, 11.99"}
                                        data-testid="bulk-add-textarea"
                                        className="mt-2 w-full border border-line bg-sand px-3 py-2 font-mono text-xs outline-none focus:border-espresso"
                                    />
                                    <button
                                        type="button"
                                        onClick={addBulkVariants}
                                        data-testid="bulk-add-confirm"
                                        className="mt-2 w-full bg-espresso py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-sand transition-colors hover:bg-gold hover:text-espresso"
                                    >
                                        Add all colors
                                    </button>
                                </div>
                            )}
                            {form.variants.length === 0 ? (
                                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-clay">No colors — this product sells as a single style</p>
                            ) : (
                                <div className="mt-3 flex flex-col gap-3" data-testid="variants-editor">
                                    {form.variants.map((v, i) => (
                                        <div key={i} className="flex items-center gap-2 border border-line bg-white p-3" data-testid={`variant-row-${i}`}>
                                            <label className="flex h-12 w-9 shrink-0 cursor-pointer items-center justify-center overflow-hidden border border-dashed border-line text-clay transition-colors hover:border-espresso" title="Color photo">
                                                {v.image ? (
                                                    <img src={imgSrc(v.image)} alt="" className="h-full w-full object-cover" data-testid={`variant-image-${i}`} />
                                                ) : (
                                                    <Upload size={12} />
                                                )}
                                                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleVariantFile(i, e)} data-testid={`variant-image-input-${i}`} />
                                            </label>
                                            <input
                                                value={v.name}
                                                onChange={(e) => setVariantField(i, "name", e.target.value)}
                                                placeholder="Ruby Red"
                                                data-testid={`variant-name-${i}`}
                                                className="w-28 border border-line px-2.5 py-2 text-xs outline-none focus:border-espresso"
                                            />
                                            <input
                                                type="number" min="0"
                                                value={v.stock}
                                                onChange={(e) => setVariantField(i, "stock", e.target.value)}
                                                placeholder="Stock"
                                                title="Stock for this color"
                                                data-testid={`variant-stock-${i}`}
                                                className="w-16 border border-line px-2.5 py-2 text-xs outline-none focus:border-espresso"
                                            />
                                            <input
                                                type="number" min="0" step="0.01"
                                                value={v.price}
                                                onChange={(e) => setVariantField(i, "price", e.target.value)}
                                                placeholder={`$${form.price || "base"}`}
                                                title="Price — leave blank to use base price"
                                                data-testid={`variant-price-${i}`}
                                                className="w-20 border border-line px-2.5 py-2 text-xs outline-none focus:border-espresso"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setForm((f) => ({ ...f, variants: f.variants.filter((_, x) => x !== i) }))}
                                                data-testid={`variant-remove-${i}`}
                                                className="ml-auto p-1.5 text-clay transition-colors hover:text-crimson"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-clay">Photo, stock &amp; price per color — blank price uses the base price</p>
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Size</label>
                                <input
                                    value={form.size}
                                    onChange={(e) => setForm((f) => ({ ...f, size: e.target.value }))}
                                    data-testid="product-size-input"
                                    className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso"
                                    placeholder="14 × 16 × 5 in"
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Weight (oz)</label>
                                <input
                                    type="number" min="0" step="0.1"
                                    value={form.weight_oz}
                                    onChange={(e) => setForm((f) => ({ ...f, weight_oz: e.target.value }))}
                                    data-testid="product-weight-input"
                                    className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso"
                                    placeholder="14"
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">USPS rate ($)</label>
                                <input
                                    type="number" min="0" step="0.01"
                                    value={form.usps_rate}
                                    onChange={(e) => setForm((f) => ({ ...f, usps_rate: e.target.value }))}
                                    data-testid="product-usps-rate-input"
                                    className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso"
                                    placeholder="5.95"
                                />
                            </div>
                        </div>
                        <p className="-mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-clay">Weight sets the live USPS rate at checkout — size &amp; weight also print on the label</p>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Category</label>
                            <input
                                list="category-options"
                                value={form.category}
                                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                                data-testid="product-category-select"
                                className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso"
                                placeholder="Totes, Jewelry, Furniture — or type your own"
                            />
                            <datalist id="category-options">
                                {CATEGORIES.map((c) => <option key={c} value={c} />)}
                            </datalist>
                            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-clay">Type anything — new categories appear in the shop and homepage menu automatically</p>
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Description</label>
                            <textarea
                                rows={4}
                                value={form.description}
                                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                                data-testid="product-description-input"
                                className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso"
                                placeholder="Tell the story of this piece…"
                            />
                        </div>
                        <label className="flex cursor-pointer items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
                            <input
                                type="checkbox"
                                checked={form.featured}
                                onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
                                data-testid="product-featured-checkbox"
                                className="h-4 w-4 accent-[#D9281C]"
                            />
                            Feature on homepage
                        </label>
                        <button
                            onClick={save}
                            disabled={saving || uploading}
                            data-testid="product-save-button"
                            className="w-full bg-espresso py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-crimson disabled:opacity-60"
                        >
                            {saving ? "Saving…" : editing ? "Save changes" : "Add product"}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog open={!!shipOrder} onOpenChange={(open) => !open && setShipOrder(null)}>
                <DialogContent className="rounded-none border-line bg-sand sm:max-w-md" data-testid="ship-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl font-medium tracking-tight">Mark as shipped</DialogTitle>
                    </DialogHeader>
                    {shipOrder && (
                        <div className="mt-2 flex flex-col gap-5">
                            <p className="text-sm text-clay">
                                {shipOrder.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")} — {shipOrder.customer_name || shipOrder.customer_email || "customer"}
                            </p>
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">USPS tracking number</label>
                                <input
                                    value={shipTracking}
                                    onChange={(e) => setShipTracking(e.target.value)}
                                    data-testid="tracking-number-input"
                                    className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso"
                                    placeholder="9400 1000 0000 0000 0000 00"
                                />
                                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-clay">The customer is emailed a tracking link automatically</p>
                            </div>
                            <button
                                onClick={submitShip}
                                disabled={shippingOrder}
                                data-testid="ship-submit-button"
                                className="w-full bg-espresso py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-gold hover:text-espresso disabled:opacity-60"
                            >
                                {shippingOrder ? "Sending…" : "Mark shipped & email customer"}
                            </button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            <Dialog open={!!labelOrder} onOpenChange={(open) => !open && setLabelOrder(null)}>
                <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none border-line bg-sand sm:max-w-lg" data-testid="label-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl font-medium tracking-tight">Create shipping label</DialogTitle>
                    </DialogHeader>
                    {labelOrder && (
                        <div className="mt-2 flex flex-col gap-5">
                            <p className="text-sm text-clay">
                                {labelOrder.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")} — {labelOrder.customer_name || "Customer"},{" "}
                                {labelOrder.shipping_address ? `${labelOrder.shipping_address.city}, ${labelOrder.shipping_address.state} ${labelOrder.shipping_address.postal_code}` : "no address"}
                            </p>
                            {labelResult ? (
                                <div className="border border-line bg-white p-6 text-center">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Label created — order marked shipped</p>
                                    <p className="mt-3 font-mono text-sm" data-testid="label-tracking">{labelResult.carrier} · {labelResult.tracking_number}</p>
                                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-clay">Customer emailed the tracking automatically</p>
                                    <a
                                        href={imgSrc(labelResult.label_path)}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="print-label-button"
                                        className="mt-5 inline-block bg-espresso px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-gold hover:text-espresso"
                                    >
                                        Print label
                                    </a>
                                </div>
                            ) : labelLoading ? (
                                <p className="font-serif text-lg italic text-clay">Asking carriers for rates…</p>
                            ) : (
                                <div className="flex flex-col gap-2" data-testid="label-rates">
                                    {labelRates.map((r, i) => (
                                        <button
                                            key={r.id}
                                            onClick={() => setLabelSelected(r)}
                                            data-testid={`label-rate-${i}`}
                                            className={`flex items-center justify-between border px-4 py-3 text-left transition-colors ${labelSelected?.id === r.id ? "border-espresso bg-espresso text-sand" : "border-line bg-white hover:border-espresso"}`}
                                        >
                                            <span className="font-mono text-xs uppercase tracking-wide">{r.provider} · {r.service}</span>
                                            <span className="flex items-center gap-3 font-mono text-xs">
                                                {r.estimated_days ? <span className={labelSelected?.id === r.id ? "text-sand/60" : "text-clay"}>{r.estimated_days}d</span> : null}
                                                <span>${r.amount.toFixed(2)}</span>
                                            </span>
                                        </button>
                                    ))}
                                    {labelRates.length === 0 && <p className="font-serif text-lg italic text-clay">No rates found for this address.</p>}
                                </div>
                            )}
                            {!labelResult && labelRates.length > 0 && (
                                <button
                                    onClick={buyLabel}
                                    disabled={labelBuying || !labelSelected}
                                    data-testid="buy-label-button"
                                    className="w-full bg-espresso py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-gold hover:text-espresso disabled:opacity-60"
                                >
                                    {labelBuying ? "Buying postage…" : `Buy label — $${labelSelected ? labelSelected.amount.toFixed(2) : "0.00"} (charged to Shippo)`}
                                </button>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            <Dialog open={!!refundOrder} onOpenChange={(open) => !open && setRefundOrder(null)}>
                <DialogContent className="rounded-none border-line bg-sand sm:max-w-md" data-testid="refund-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl font-medium tracking-tight">Refund order</DialogTitle>
                    </DialogHeader>
                    {refundOrder && (
                        <div className="mt-2 flex flex-col gap-5">
                            <p className="text-sm text-clay">
                                {refundOrder.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")} — {refundOrder.customer_name || refundOrder.customer_email}
                            </p>
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Refund amount (max ${(refundOrder.amount_total - (refundOrder.refunded_total || 0)).toFixed(2)})</label>
                                <input
                                    type="number" min="0.01" step="0.01"
                                    value={refundAmount}
                                    onChange={(e) => setRefundAmount(e.target.value)}
                                    data-testid="refund-amount-input"
                                    className="mt-2 w-full border border-line bg-white px-4 py-3 text-sm outline-none focus:border-espresso"
                                />
                                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-clay">Goes straight back to the customer's card — customer is emailed automatically</p>
                            </div>
                            <button
                                onClick={submitRefund}
                                disabled={refunding}
                                data-testid="refund-submit-button"
                                className="w-full bg-crimson py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-espresso disabled:opacity-60"
                            >
                                {refunding ? "Refunding…" : `Refund $${parseFloat(refundAmount || 0).toFixed(2)}`}
                            </button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </main>
    );
}
